import { FC } from 'react';
import { useHistory } from 'react-router';
import { Flex, Image, VStack } from '@chakra-ui/react';

import folderImg from 'assets/brand/folder@2x.png';
import Button from 'components/_core/Button';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import { logEvent } from 'features/Analytics/analytics';
import CardsGroupDescription from 'features/Payments/Pages/Transfers/components/CardsGroupDescription';
import ListSkeleton from 'features/Payments/Pages/Transfers/components/ListSkeleton';
import routes from 'features/Payments/Pages/Transfers/routes';

import { BeneficiarySection } from '../../types';
import BeneficiaryRow from '../BeneficiaryRow';
import locales from './locales';

export interface BeneficiariesListProps {
  beneficiaries: BeneficiarySection[];
  hasMoreToFetch: boolean;
  isLoading: boolean;
  loadMore: () => void;
  onBeneficiaryClick: (bankTransferRecipientId: string) => void;
}

const BeneficiariesList: FC<BeneficiariesListProps> = ({
  beneficiaries,
  hasMoreToFetch,
  isLoading,
  loadMore,
  onBeneficiaryClick,
}) => {
  const history = useHistory();

  const hasBeneficiaries = beneficiaries.length > 0;

  if (isLoading && !hasBeneficiaries) {
    return <ListSkeleton />;
  }

  if (!hasBeneficiaries) {
    return (
      <VStack alignItems="center" paddingTop="space-24" spacing="space-32">
        <Image alt="" boxSize="7.5rem" role="presentation" src={folderImg} />
        <VStack alignItems="center" spacing="space-8">
          <Text fontSize="font-18" fontWeight="weight-500">
            {locales.noBeneficiaries}
          </Text>
          <Button
            icon="arrow-right"
            onClick={() => {
              logEvent({ name: 'Click_Add_Beneficiary' });
              history.push(routes.newBeneficiary);
            }}
            variant="inline-primary"
          >
            {locales.addFirstBeneficiary}
          </Button>
        </VStack>
      </VStack>
    );
  }

  return (
    <>
      {beneficiaries.map(([key, section]) => (
        <Flex
          flexDirection="column"
          key={`bank-transfer-recipient-list-${key}-section`}
          marginBottom="space-32"
        >
          <CardsGroupDescription
            description={`${locales.sections[key]} (${section.length})`}
          />

          <SunshineCard.Group>
            {section.map((beneficiary) => (
              <BeneficiaryRow
                beneficiary={beneficiary}
                key={beneficiary.bankTransferRecipientId}
                onClick={() =>
                  onBeneficiaryClick(beneficiary.bankTransferRecipientId)
                }
              />
            ))}
          </SunshineCard.Group>

          {hasMoreToFetch ? (
            <Button
              alignSelf="center"
              isLoading={isLoading}
              marginTop="space-24"
              onClick={loadMore}
              variant="secondary"
            >
              {locales.seeMore}
            </Button>
          ) : null}
        </Flex>
      ))}
    </>
  );
};

export default BeneficiariesList;
